import React from 'react';
import { motion } from 'framer-motion';
import { FiGlobe, FiLayers, FiMonitor, FiSmartphone, FiPenTool, FiServer, FiSearch, FiZap, FiMapPin, FiHome } from 'react-icons/fi';
import './Services.css';

const itServices = [
  {
    icon: <FiGlobe />,
    title: 'Веб-сайты',
    desc: 'Разрабатываем корпоративные сайты, landing pages и порталы. Современный дизайн, быстрая загрузка, SEO-оптимизация и адаптивность для всех устройств.',
    features: ['Корпоративные сайты', 'Landing pages', 'Интернет-магазины', 'Порталы и каталоги'],
    color: '#3b82f6',
  },
  {
    icon: <FiLayers />,
    title: 'Веб-приложения',
    desc: 'Создаём SPA, PWA, личные кабинеты и системы управления. Сложная бизнес-логика, интеграции с API, real-time функциональность.',
    features: ['SPA / PWA', 'CRM и ERP системы', 'Личные кабинеты', 'Дашборды и аналитика'],
    color: '#2563eb',
  },
  {
    icon: <FiMonitor />,
    title: 'Desktop-приложения',
    desc: 'Кроссплатформенные desktop-решения для Windows, macOS и Linux. Нативный UI, работа офлайн, системные интеграции.',
    features: ['Windows / macOS / Linux', 'Кроссплатформенность', 'Работа офлайн', 'Системные интеграции'],
    color: '#1d4ed8',
    highlight: true,
  },
  {
    icon: <FiSmartphone />,
    title: 'Мобильные приложения',
    desc: 'Разработка нативных и кроссплатформенных мобильных приложений для iOS и Android. Чистый UX, высокая производительность и публикация в App Store / Google Play.',
    features: ['iOS & Android', 'React Native / Flutter', 'Push-уведомления', 'Офлайн-режим'],
    color: '#0891b2',
  },
  {
    icon: <FiPenTool />,
    title: 'UI/UX Дизайн',
    desc: 'Проектируем интерфейсы, которые удобны и красивы. Прототипирование, дизайн-системы и пользовательские исследования для продуктов любой сложности.',
    features: ['Прототипирование в Figma', 'Дизайн-системы', 'UX-аудит', 'Брендинг и айдентика'],
    color: '#7c3aed',
  },
  {
    icon: <FiServer />,
    title: 'IT-инфраструктура',
    desc: 'Проектирование и сопровождение IT-инфраструктуры: облачные среды, CI/CD, контейнеризация и мониторинг. Надёжная основа для ваших цифровых продуктов.',
    features: ['Docker / Kubernetes', 'CI/CD пайплайны', 'Облако: AWS, GCP, VPS', 'Мониторинг и логирование'],
    color: '#059669',
  },
  {
    icon: <FiSearch />,
    title: 'IT-аудит и консалтинг',
    desc: 'Анализируем существующие системы, выявляем узкие места и предлагаем оптимальную техническую архитектуру. Помогаем выбрать стек и составить roadmap проекта.',
    features: ['Аудит кода и архитектуры', 'Техническое задание', 'Выбор технологий', 'Roadmap и оценка'],
    color: '#d97706',
  },
];

const engServices = [
  {
    icon: <FiZap />,
    title: 'Проектирование электроснабжения',
    desc: 'Разрабатываем проекты систем электроснабжения для объектов любой сложности: жилые дома, коммерческие объекты, промышленные предприятия и общественные здания.',
    features: ['Частные дома и квартиры', 'Коммерческие объекты', 'Промышленные предприятия', 'Внешнее электроснабжение (ВЛ/КЛ)'],
    color: '#f59e0b',
  },
  {
    icon: <FiMapPin />,
    title: 'Инженерно-геодезические изыскания',
    desc: 'Выполняем топографические и исполнительные съёмки, межевание участков, вынос границ в натуру, съёмку с помощью БПЛА (дронов) и кадастровые работы.',
    features: ['Топографические съёмки', 'Исполнительные съёмки', 'Межевание участков', 'Съёмка дронами / 3D-модели'],
    color: '#10b981',
  },
  {
    icon: <FiHome />,
    title: 'Проектирование зданий и дизайн',
    desc: 'Разрабатываем архитектурные решения для жилых, коммерческих и промышленных объектов. Современный дизайн интерьеров с учётом всех требований заказчика.',
    features: ['Жилые и коммерческие объекты', 'Дизайн интерьеров', 'Индивидуальный подход', 'Полный пакет документации'],
    color: '#8b5cf6',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

export default function Services() {
  return (
    <section className="section services" id="services">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="tag">ЧТО МЫ ДЕЛАЕМ</div>
          <div className="section-divider" style={{ marginTop: 16 }} />
          <h2 className="section-title">Наши <span>услуги</span></h2>
          <p className="section-subtitle">
            Полный цикл цифровых решений — от консалтинга и дизайна до разработки
            и поддержки инфраструктуры. Работаем со стартапами и бизнесом любого масштаба.
          </p>
        </motion.div>

        {/* IT Services */}
        <div className="services__group-label">
          <span>—  IT-разработка и ПО</span>
        </div>
        <motion.div
          className="services__grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {itServices.map((s, i) => (
            <motion.div
              key={i}
              className={`services__card card${s.highlight ? ' services__card--highlight' : ''}`}
              variants={cardVariants}
              style={{ '--card-accent': s.color }}
            >
              <div className="services__icon">{s.icon}</div>
              <h3 className="services__title">{s.title}</h3>
              <p className="services__desc">{s.desc}</p>
              <ul className="services__features">
                {s.features.map((f, j) => (
                  <li key={j}>
                    <span className="services__check">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Engineering Services */}
        <div className="services__group-label" style={{ marginTop: 48 }}>
          <span>—  Инженерное проектирование и геодезия</span>
        </div>
        <motion.div
          className="services__grid services__grid--eng"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {engServices.map((s, i) => (
            <motion.div
              key={i}
              className="services__card card"
              variants={cardVariants}
              style={{ '--card-accent': s.color }}
            >
              <div className="services__icon">{s.icon}</div>
              <h3 className="services__title">{s.title}</h3>
              <p className="services__desc">{s.desc}</p>
              <ul className="services__features">
                {s.features.map((f, j) => (
                  <li key={j}>
                    <span className="services__check">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
